import React, { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { bookNow } from "../../store/Cab/CabAction"
import { cabAction } from "../../store/Cab/CAbReducer"
import { useNavigate } from "react-router-dom"
import Hatchback from "../../assets/cars/hatchback.webp"
import Sedan from "../../assets/cars/sedan.webp"
import SUV from "../../assets/cars/suv.webp"

const CAB_TYPE = {
  1: "Hatchback",
  2: "Sedan",
  3: "SUV",
}

const CAB_TAGLINE = {
  1: "Budget friendly",
  2: "Most popular",
  3: "Premium comfort",
}

const MODEL_NAMES = {
  1: "Swift, WagonR or similar",
  2: "Dzire, Etios or similar",
  3: "Innova, Ertiga or similar",
}

const BAGS = { 1: 2, 2: 3, 3: 4 }

// The fare API returns cab.name reliably but sometimes omits vehicle_id. Map the
// name back to an id so the title / model / tagline don't all fall back to
// "Hatchback" when vehicle_id is missing.
const NAME_TO_ID = { hatchback: 1, sedan: 2, suv: 3 }

const inr = (n) => Number(parseInt(n) || 0).toLocaleString("en-IN")

const Spec = ({ path, children, sw = 2 }) => (
  <span className="pm-cab__spec">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {path}
    </svg>
    {children}
  </span>
)

const CabCard = ({ showPrice, cab, roundTrip = false }) => {
  const dispatch = useDispatch()
  const { bookingSuccess, visitorEntryId, web_trip_unique_id, distance } =
    useSelector((state) => state.cab)
  const navigate = useNavigate()

  // Round trips quote the RUNNING fare up front. Driver allowance and toll/parking
  // are paid at the trip (at actuals), so they are shown as extras and are neither
  // in the headline price nor in the amount collected online. One-way (fixed city
  // rate) and local stay a single all-inclusive price. `total - driver_allowance`
  // keeps base + allowance reconciling to the original all-in figure.
  const cabTotal = cab?.total || ((Number(cab?.subtotal) || 0) + (Number(cab?.gst) || 0))
  const driverAllowance = Number(cab?.driver_allowance) || 0
  const headlineTotal = roundTrip ? Math.max(cabTotal - driverAllowance, 0) : cabTotal

  useEffect(() => {
    if (bookingSuccess) {
      dispatch(cabAction.clearBookingSuccess())
      navigate("/bookCab")
    }
  }, [bookingSuccess, dispatch, navigate])

  // Booking payload and side effects are unchanged: this is the exact contract
  // the payment page (/bookCab) and the backend already depend on.
  const submitHandle = (e) => {
    e.stopPropagation()
    const currentTripData = JSON.parse(localStorage.getItem("tripData")) || {}
    const updatedTripData = { ...currentTripData, cab: { ...cab } }
    localStorage.setItem("tripData", JSON.stringify(updatedTripData))

    const data = {
      visitorEntryId,
      web_trip_unique_id,
      vehicle_id: cab?.vehicle_id,
      // The fare API returns this as `total`. Reading `grand_total` here yielded
      // undefined, Sequelize skipped the column, and every booking made through
      // this card was stored with a NULL fare. For a round trip this is the base
      // running fare (driver allowance + toll are collected at the trip), so it
      // matches the headline the rider saw and the amount charged on /bookCab.
      grand_total: headlineTotal,
      advance_booking: 20,
      advance_booking_amount: 500,
      GST: cab?.gst_percentage,
      GSTCalculate: cab?.gst,
      distance,
      estimate_fare: roundTrip ? Math.max((Number(cab?.subtotal) || 0) - driverAllowance, 0) : cab?.subtotal,
    }
    dispatch(bookNow(data))
  }

  const cabNameLower = (cab?.name || "").toLowerCase().replace(/\s+/g, "")
  const carImage = cabNameLower === "hatchback"
    ? Hatchback
    : cabNameLower === "suv"
      ? SUV
      : Sedan

  // The name ("Hatch Back" / "Sedan" / "SUV") is the reliable discriminator.
  // The API's vehicle_id is a backend DB id (e.g. 49/50/51 on the fleets
  // backend), not the 1/2/3 type, so using it first mislabelled every card
  // "Hatchback". Map from the name, and only trust vehicle_id when it is a
  // real 1-3 type id.
  const vehId =
    NAME_TO_ID[cabNameLower] ||
    (cab?.vehicle_id >= 1 && cab?.vehicle_id <= 3 ? cab.vehicle_id : 1)
  const cabTypeLabel = CAB_TYPE[vehId] || "Hatchback"
  const tagline = CAB_TAGLINE[vehId] || ""
  const modelNames = MODEL_NAMES[vehId] || MODEL_NAMES[1]
  const isPopular = vehId === 2
  const km = Math.ceil(distance) || 0
  // Rentals carry hourly-package fields (package_hours / km_included); outstation
  // carries trip distance. The API echoes package hours into the top-level
  // `distance`, so never show that as km on a rental card.
  const isRentalCab = cab?.package_hours != null || cab?.km_included != null
  // Local = airport/station transfer: an all-inclusive distance-based fare, so
  // it shows the transfer distance, not a "km included" / per-km-after line.
  const isLocalFlat = !isRentalCab && cab?.local_base_fare != null
  const includedKm = Number(cab?.km_included) || 0
  const packageHours = Number(cab?.package_hours) || 0
  const transferKm = Number(cab?.transfer_km) || 0
  const afterKm = isRentalCab ? includedKm : km
  const showKmChip = isRentalCab
    ? packageHours > 0 || includedKm > 0
    : isLocalFlat
    ? transferKm > 0
    : km > 0
  const kmChipLabel = isRentalCab
    ? [packageHours > 0 ? `${packageHours}h` : null, includedKm > 0 ? `${includedKm} km` : null]
        .filter(Boolean)
        .join(" · ") + " incl."
    : isLocalFlat
    ? `${transferKm} km`
    : `${km} km included`

  return (
    <div className={`pm-cab pm-cab--${cabNameLower || "sedan"}${isPopular ? " pm-cab--popular" : ""}`}>
      {isPopular && <span className="pm-cab__ribbon">Most popular</span>}

      <div className="pm-cab__imgwrap">
        <img src={carImage} className="pm-cab__img" alt={`${cabTypeLabel} cab`} loading="lazy" decoding="async" />
      </div>

      <div className="pm-cab__head">
        <div>
          <h3 className="pm-cab__name">{cabTypeLabel}</h3>
          <span className="pm-cab__model">{modelNames}</span>
        </div>
        {tagline && <span className="pm-cab__tag">{tagline}</span>}
      </div>

      <div className="pm-cab__specs">
        <Spec path={<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></>}>
          {cab?.seat_capacity || 4} seats
        </Spec>
        <Spec path={<><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /></>}>
          {BAGS[vehId] || 2} bags
        </Spec>
        <Spec path={<path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2" />}>
          AC
        </Spec>
        {showKmChip && (
          <Spec path={<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>}>
            {kmChipLabel}
          </Spec>
        )}
      </div>

      <div className="pm-cab__foot">
        {showPrice && (
          <div className="pm-cab__price">
            <span className="pm-cab__amt">&#8377;{inr(headlineTotal)}</span>
            <span className="pm-cab__amt-label">
              {roundTrip
                ? "Base fare · GST included"
                : <>All inclusive &middot; incl. &#8377;{inr(cab?.gst)} GST</>}
            </span>
          </div>
        )}
        <button className="pm-cab__btn" onClick={submitHandle}>
          Book {cabTypeLabel}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>

      {roundTrip ? (
        <div className="pm-cab__extras">
          {driverAllowance > 0 && (
            <span className="pm-cab__extra">
              <span>Driver allowance</span>
              <span className="pm-cab__extra-val">+ &#8377;{inr(driverAllowance)}</span>
            </span>
          )}
          <span className="pm-cab__extra">
            <span>Toll, parking &amp; state tax</span>
            <span className="pm-cab__extra-val">At actuals</span>
          </span>
        </div>
      ) : (
        (isLocalFlat || (cab?.fare_per_km && afterKm > 0)) && (
          <div className="pm-cab__note">
            {isLocalFlat
              ? <>Doorstep pickup</>
              : <>&#8377;{cab.fare_per_km}/km after {afterKm} km</>}
          </div>
        )
      )}
    </div>
  )
}

export default CabCard
